import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {useState} from 'react';
import {useRef} from 'react';
import React from 'react';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {ControlledSlider} from '@Components/controlled-slider';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import styles from './download-speed-option.module.scss';
import {isPosterVideo} from '@Libraries/poster-object.library';

const MIN_SPEED = 0.25;
const MAX_SPEED = 4;
const STEP = 0.25;
const DEBOUNCE_DELAY = 500;

export function DownloadSpeedOption(): ReactElement | null {
  const {speed, setSpeed, posterObject} = useDownloadPanelData();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [speedTemp, setSpeedTemp] = useState<number>(speed);
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => {
    if (!isPosterVideo(posterObject) && speed !== 1) {
      setSpeed(1);
    }
  }, [posterObject]);

  if (!isPosterVideo(posterObject)) {
    return null;
  }

  const roundAndClampSpeed = (value: number): number => {
    const roundedValue = Math.round(value / STEP) * STEP;
    return Math.max(MIN_SPEED, Math.min(roundedValue, MAX_SPEED));
  };

  const onSpeedChange = (value: number): void => {
    clearTimeout(timeoutRef.current);
    setIsUpdating(true);
    setSpeedTemp(value);
    timeoutRef.current = setTimeout(() => {
      const finalValue = roundAndClampSpeed(value);
      setSpeedTemp(finalValue);
      setSpeed(finalValue);
      setIsUpdating(false);
    }, DEBOUNCE_DELAY);
  };

  const onSlideChange = (value: number): void => {
    setIsUpdating(true);
    setSpeedTemp(value);
    setSpeed(value);
  };

  return (
    <div className="flex flex-v-row">
      <div className="flex-h-row flex-justify-between flex-items-center spacing-m-b-2">
        <Text val={window.i18next.t('pmwjs_speed')} bold size={TextSize.XSMALL} />
        <div className="flex-h-row">
          <InputField className={styles.bleedInput} size={InputFieldSize.XSMALL} input={isUpdating ? speedTemp : speed} onInputChange={onSpeedChange} />
        </div>
      </div>
      <ControlledSlider
        min={MIN_SPEED}
        max={MAX_SPEED}
        step={STEP}
        value={isUpdating ? speedTemp : speed}
        onSlide={onSlideChange}
        onSlideEnded={(): void => {
          setIsUpdating(false);
        }}
      />
    </div>
  );
}
